import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useCompatibilityChat } from "./useCompatibilityChat";
import {
  ArrowLeft,
  Send,
  Heart,
  Sparkles,
  User,
  MoreVertical,
} from "lucide-react";

interface LocationState {
  otherUserName?: string;
}

export const CompatibilityChatScreen: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const location = useLocation();
  const otherUserName =
    (location.state as LocationState | null)?.otherUserName || "Your Match";

  const { user } = useAuth();
  const { messages, loading, sending, sendMessage } =
    useCompatibilityChat(requestId);

  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    try {
      await sendMessage(text);
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-rose-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>

      {/* Fixed Header */}
      <div className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/50 shadow-lg shadow-rose-100/30 flex-shrink-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/history"
              className="p-2 hover:bg-rose-50 rounded-full transition-all duration-200 group"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-rose-400 transition-colors" />
            </Link>

            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-gradient-to-r from-rose-400 to-purple-400 flex items-center justify-center text-white text-base font-bold shadow-lg shadow-rose-200">
                {otherUserName.charAt(0).toUpperCase()}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white"></div>
            </div>

            <div>
              <h1 className="text-base font-semibold text-gray-900">
                {otherUserName}
              </h1>
              <p className="text-xs text-green-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block"></span>
                Online
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button className="p-2 hover:bg-rose-50 rounded-full transition-all duration-200">
              <Heart className="w-5 h-5 text-gray-400 hover:text-rose-400 transition-colors" />
            </button>
            <button className="p-2 hover:bg-rose-50 rounded-full transition-all duration-200">
              <MoreVertical className="w-5 h-5 text-gray-400 hover:text-rose-400 transition-colors" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 relative z-10"
        style={{
          scrollBehavior: "smooth",
          maxHeight: "calc(100vh - 140px)",
        }}
      >
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-rose-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-10 h-10 border-4 border-rose-400 rounded-full border-t-transparent animate-spin"></div>
            </div>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-r from-rose-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-rose-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Start the Conversation
            </h3>
            <p className="text-sm text-gray-400 max-w-xs">
              Say hello and begin your journey of connection with{" "}
              {otherUserName}
            </p>
          </div>
        )}

        {messages.map((msg, index) => {
          const isMine = msg.senderId === user?._id;
          const showTime =
            index === 0 ||
            (index > 0 &&
              new Date(msg.createdAt).getTime() -
                new Date(messages[index - 1].createdAt).getTime() >
                300000);

          return (
            <div key={msg._id} className="space-y-1">
              {showTime && (
                <div className="flex justify-center">
                  <span className="text-xs text-gray-400 bg-white/50 px-3 py-1 rounded-full backdrop-blur-sm">
                    {formatTime(msg.createdAt)}
                  </span>
                </div>
              )}
              <div
                className={`flex ${isMine ? "justify-end" : "justify-start"} animate-fadeIn`}
              >
                {!isMine && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-rose-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 shadow-lg shadow-rose-200">
                    {otherUserName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm relative ${
                    isMine
                      ? "bg-gradient-to-r from-rose-400 to-purple-400 text-white rounded-br-sm shadow-lg shadow-rose-200"
                      : "bg-white/90 backdrop-blur-sm text-gray-800 rounded-bl-sm shadow-md shadow-gray-100/50 border border-white/50"
                  }`}
                >
                  {msg.text}
                  <div
                    className={`text-[10px] mt-1 opacity-60 ${
                      isMine ? "text-white/80 text-right" : "text-gray-400"
                    }`}
                  >
                    {formatTime(msg.createdAt)}
                  </div>
                </div>
                {isMine && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold ml-2 flex-shrink-0 shadow-lg shadow-purple-200">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Fixed Footer with Input */}
      <div className="relative z-10 bg-white/80 backdrop-blur-xl border-t border-white/50 shadow-lg shadow-rose-100/30 flex-shrink-0">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              className="w-full bg-gray-50/80 rounded-2xl px-5 py-3 text-sm border-2 border-gray-200 focus:border-rose-300 focus:outline-none focus:ring-4 focus:ring-rose-100 transition-all duration-200 placeholder:text-gray-400 pr-12"
              placeholder="Type a message..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            {draft.trim() && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <span className="text-xs text-rose-400 font-medium animate-pulse">
                  ●
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 shadow-lg ${
              sending || !draft.trim()
                ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-gray-100"
                : "bg-gradient-to-r from-rose-400 to-purple-400 text-white hover:scale-105 active:scale-95 shadow-rose-200 hover:shadow-xl hover:shadow-rose-300"
            }`}
            onClick={handleSend}
            disabled={sending || !draft.trim()}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CompatibilityChatScreen;
